// Network notices.
//
// PTV publishes a class of notice that is not a fault on a line: "special event
// trains are running to the Showgrounds for the Royal Show", "Grand Final Eve
// public holiday timetable", "extra services for the AFL finals". They arrive
// in the same disruptions feed as everything else, filed against every line
// they touch — the Royal Show notice sits on all sixteen metro lines — and
// `severityOf` rightly calls them a `notice`, which is below the line this app
// ever buzzes a phone for.
//
// They are worth knowing, though, and PTV's own app pushes them. So they get a
// place of their own: ONE card on the Alerts screen however many lines carry
// them, and ONE push, opt-in, from `server/push.mjs`. This file is what both
// sides agree a network notice is, so the card and the push cannot disagree.
//
// Import-light on purpose, like severity.js: the server imports it too.

import { severityOf } from './severity.js';

/**
 * PTV's own types for news about the network rather than a fault on a line.
 * Verbatim, as `disruption_type` spells them.
 *
 * "Service Information" is deliberately NOT here: in practice it is one named
 * service cancelled or ending early, which `severityOf` already reads as a
 * cancellation and the ordinary alert path already sends.
 */
export const NOTICE_TYPES = new Set(['Travel Alert', 'Special Event', 'Timetable/Route Changes']);

/** Is this row a network notice, rather than a fault or a building? */
export function isNetworkNotice(row, now = Date.now()) {
  if (!row || !NOTICE_TYPES.has(String(row.type ?? ''))) return false;
  // A station-scoped notice is about a platform, a car park or a bus stop.
  if (row.stopScoped) return false;
  // Anything the severity reader thinks is worse than a notice is already an
  // alert, and belongs to the ordinary path — never both.
  return severityOf(row, now) === 'notice';
}

/**
 * The feed's per-line rows, collapsed back to one entry per PTV notice.
 *
 * Accepts either the server's rows (`routeId`) or the app's stored records
 * (`line`). Current notices first, then planned; newest first within each.
 */
export function networkNotices(rows, now = Date.now()) {
  const byId = new Map();
  for (const row of rows ?? []) {
    if (!isNetworkNotice(row, now)) continue;
    const key = String(row.ptvId);
    const routeId = Number(row.routeId ?? row.line);
    const existing = byId.get(key);
    if (existing) {
      if (Number.isFinite(routeId) && !existing.routeIds.includes(routeId)) existing.routeIds.push(routeId);
      continue;
    }
    byId.set(key, {
      ptvId: Number(row.ptvId),
      id: row.id ?? null,
      type: String(row.type),
      title: String(row.title ?? ''),
      description: String(row.description ?? ''),
      status: row.status === 'planned' ? 'planned' : 'current',
      startedAt: row.startedAt ?? null,
      url: row.url ?? '',
      routeIds: Number.isFinite(routeId) ? [routeId] : [],
    });
  }
  const at = (n) => Date.parse(n.startedAt ?? '') || 0;
  return [...byId.values()].sort((a, b) =>
    (a.status === b.status ? 0 : a.status === 'current' ? -1 : 1) || at(b) - at(a));
}

/** The push for one notice: PTV's type as the title, its words as the body. */
export function noticeMessage(notice) {
  const text = (notice.description || notice.title).replace(/\s+/g, ' ').trim();
  return {
    title: notice.type,
    body: text.length > 220 ? `${text.slice(0, 219).trimEnd()}…` : text,
    routeIds: [...notice.routeIds],
    disruptionIds: [notice.ptvId],
  };
}

/**
 * ONE push for however many notices a device is owed in a sweep.
 *
 * A single notice is sent as itself. Several are counted in the title, and the
 * body is the first of them — `networkNotices` orders current before planned
 * and newest first, so that is the one most likely to change a trip today.
 * Every id travels with it, so a tap can open the Alerts tab on all of them.
 */
export function noticeBatch(notices) {
  const list = [...(notices ?? [])];
  if (!list.length) return null;
  if (list.length === 1) return noticeMessage(list[0]);
  const first = noticeMessage(list[0]);
  return {
    title: `${list.length} network notices`,
    body: first.body,
    routeIds: [...new Set(list.flatMap((n) => n.routeIds ?? []).map(Number))],
    disruptionIds: [...new Set(list.map((n) => Number(n.ptvId)))],
  };
}
